import { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import type { VariablesOf } from 'gql.tada';
import { UserPlus, AlertCircle, Trash2, Save, X, CheckCircle, XCircle, FileText, ExternalLink, Send, Eye, RotateCcw, Clock, UserCheck, UserX, ListTodo, Settings2, MinusCircle } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { Checkbox } from '~/components/ui/checkbox';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { EnrollmentStatusBadge } from '~/components/enrollment';
import { DeleteStudentEnrollmentMutation } from '../Enrollments/useDeleteStudentEnrollment';
import { DeleteFamilyEnrollmentMutation } from '../Enrollments/useDeleteFamilyEnrollment';
import { isNewStyle } from '../Enrollments/enrollmentPhases';
import { QueueFamilyForInviteMutation } from '../Enrollments/useQueueFamilyForInvite';
import { UnqueueFamilyMutation } from '../Enrollments/useUnqueueFamily';
import { UpdateFamilyEnrollmentMutation } from './useUpdateFamilyEnrollment';
import { EnrollmentFeeSetting } from './EnrollmentFeeSetting';
import { StudentStatusMenu } from './StudentStatusMenu';
import { useSetStudentEnrollmentStatus, SetStudentEnrollmentStatusMutation } from '../StudentDetail/useSetStudentEnrollmentStatus';
import { UpdateStudentEnrollmentMutation } from '../Enrollments/useUpdateStudentEnrollment';
import { DeclineFamilyEnrollmentMutation } from './useDeclineFamilyEnrollment';
import { SendEnrollmentPacketMutation } from './useSendEnrollmentPacket';
import { SendFormBackToFamilyMutation } from './useSendFormBackToFamily';
import { AcceptStudentEnrollmentMutation } from './useAcceptStudentEnrollment';
import { WaitlistStudentEnrollmentMutation } from './useWaitlistStudentEnrollment';
import { DenyStudentEnrollmentMutation } from './useDenyStudentEnrollment';
import { WithdrawStudentEnrollmentMutation } from './useWithdrawStudentEnrollment';
import { RevokeReenrollmentInviteMutation } from './useRevokeReenrollmentInvite';
import { ResendFamilyInvite } from './useResendFamilyInvite';
import { RevokeEnrollmentPacketMutation } from './useRevokeEnrollmentPacket';
import { CompleteEnrollmentReviewMutation } from './useCompleteEnrollmentReview';
import { UndoEnrollmentReviewMutation } from './useUndoEnrollmentReview';
import { AdminReenrollmentReview } from './AdminReenrollmentReview';
import { AdminAdmissionsReview } from './AdminAdmissionsReview';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { ChecklistCard } from './ChecklistCard';
import { InvoicesSection } from './InvoicesSection';
import { AddChildToYearAction } from './AddChildToYearDialog';
import { InviteToEnrollAction } from './InviteToEnrollDialog';
import {
  getAvailableStudentEnrollmentActions,
  hasAvailableActions
} from '~/utils/studentEnrollmentActions';
import type {
  FamilyEnrollmentTabProps,
  FamilyEnrollment,
  StudentEnrollment,
  GradeLevel,
  PaymentOption,
} from './types';

const FAMILY_ENROLLMENT_KINDS = [
  { value: 'NEW_ADMISSION', label: 'New Admission' },
  { value: 'RE_ENROLLMENT', label: 'Re-enrollment' },
];

const STUDENT_ENROLLMENT_KINDS = [
  { value: 'NEW_STUDENT', label: 'New Student' },
  { value: 'RETURNING_STUDENT', label: 'Returning Student' },
];

interface EmptyEnrollmentStateProps {
  selectedYear: { name: string } | null;
  familyName: string;
  onStartEnrollment: () => void;
}

function EmptyEnrollmentState({ selectedYear, familyName, onStartEnrollment }: EmptyEnrollmentStateProps) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">
          No Enrollment for {selectedYear?.name}
        </h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          {familyName} does not have an enrollment for the {selectedYear?.name} school year yet.
          Create one to start tracking their enrollment status.
        </p>
        <Button onClick={onStartEnrollment}>
          <UserPlus className="h-4 w-4 mr-2" />
          Start Enrollment
        </Button>
      </CardContent>
    </Card>
  );
}

interface EditableFamilyEnrollmentCardProps {
  enrollment: FamilyEnrollment;
  /** With the checklist on, the status is the year's access, as on the Enrollments board. */
  checklistEnrollmentOn?: boolean;
  editedData: Record<string, any>;
  onDataChange: (field: string, value: any) => void;
  hasChanges: boolean;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function EditableFamilyEnrollmentCard({
  enrollment,
  checklistEnrollmentOn = false,
  editedData,
  onDataChange,
  hasChanges,
  onSave,
  onCancel,
  isSaving
}: EditableFamilyEnrollmentCardProps) {
  return (
    <Card className={hasChanges ? 'ring-2 ring-primary' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between">
          <span>Enrollment Summary</span>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <>
                <Button variant="outline" size="sm" onClick={onCancel} disabled={isSaving}>
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button size="sm" onClick={onSave} disabled={isSaving}>
                  <Save className="h-4 w-4 mr-1" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Status</Label>
            <div className="h-8 flex items-center">
              {/* With the checklist on, every year reads its access, as one opened by the checklist invite does. */}
              <EnrollmentStatusBadge
                state={enrollment.state}
                access={enrollment.access}
                accessSource={checklistEnrollmentOn ? 'SET' : enrollment.accessSource}
                type="family"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Type</Label>
            <Select
              value={editedData.enrollmentKind ?? enrollment.enrollmentKind}
              onValueChange={(value: string) => onDataChange('enrollmentKind', value)}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FAMILY_ENROLLMENT_KINDS.map((kind) => (
                  <SelectItem key={kind.value} value={kind.value}>
                    {kind.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Students</Label>
            <p className="font-medium h-8 flex items-center">{enrollment.studentCount || 0}</p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Inquiry</Label>
            <div className="flex items-center gap-2 h-8">
              <Checkbox
                checked={editedData.isInquiry ?? enrollment.isInquiry ?? false}
                onCheckedChange={(checked: boolean) => onDataChange('isInquiry', checked)}
              />
              <span className="text-sm">{(editedData.isInquiry ?? enrollment.isInquiry) ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>
        {/* Financial Assistance Indicator */}
        {(enrollment.metadata as Record<string, any>)?.seekingFinancialAssistance && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Family is seeking financial assistance</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Student disposition options for the review modal
const DISPOSITION_OPTIONS = [
  { value: 'accept', label: 'Accept', description: 'Student will proceed to enrollment' },
  { value: 'waitlist', label: 'Waitlist', description: 'Place student on waitlist' },
  { value: 'deny', label: 'Deny', description: 'Deny enrollment' },
  { value: 'not_returning', label: 'Not Returning', description: 'Family chose not to re-enroll this student' },
];

// Modal for completing enrollment review with per-student disposition decisions
// Fetches form data to include unpersisted new students from the reenrollment form
interface CompleteReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrollment: FamilyEnrollment;
  onComplete: (studentDispositions: any[]) => void;
  completing: boolean;
}

function CompleteReviewModal({ open, onOpenChange, enrollment, onComplete, completing }: CompleteReviewModalProps) {
  const [dispositions, setDispositions] = useState<Record<string, string>>({});

  // Build student list directly from model data (works for both reenrollment and application)
  // After form submission, the applier has already created all StudentEnrollment records.
  // - enrollmentKind: RETURNING vs NEW_STUDENT
  // - state: NOT_RETURNING means parent said they won't return (reenrollment only)
  const studentsToReview = useMemo(() => {
    if (!enrollment?.studentEnrollments) return [];

    return enrollment.studentEnrollments.map((se: any) => ({
      id: se.id,
      type: 'existing',
      firstName: se.person.firstName,
      lastName: se.person.lastName,
      gradeLevel: se.gradeLevel,
      isReturning: se.enrollmentKind === 'RETURNING' && se.state !== 'NOT_RETURNING',
      isNotReturning: se.state === 'NOT_RETURNING',
      isNew: se.enrollmentKind === 'NEW_STUDENT',
    }));
  }, [enrollment?.studentEnrollments]);

  // Reset dispositions when students list changes
  useEffect(() => {
    if (studentsToReview.length > 0) {
      const initial: Record<string, string> = {};
      studentsToReview.forEach((student: any) => {
        // Default disposition based on student status
        if (student.isNotReturning) {
          initial[student.id] = 'not_returning';
        } else {
          initial[student.id] = 'accept';
        }
      });
      setDispositions(initial);
    }
  }, [studentsToReview]);

  const handleDispositionChange = (studentId: string, action: string) => {
    setDispositions(prev => ({ ...prev, [studentId]: action }));
  };

  const handleSubmit = () => {
    const studentDispositions = studentsToReview.map((student: any) => {
      const disposition: any = { action: dispositions[student.id] || 'accept' };

      disposition.studentEnrollmentId = student.id;

      return disposition;
    });
    onComplete(studentDispositions);
  };

  // Count dispositions for summary
  const counts = Object.values(dispositions).reduce((acc: Record<string, number>, action) => {
    acc[action] = (acc[action] || 0) + 1;
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTodo className="h-5 w-5" />
            Complete Enrollment Review
          </DialogTitle>
          <DialogDescription>
            Review each student and decide whether to accept, waitlist, or deny their enrollment.
            Invoices will be generated for accepted students.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {studentsToReview.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No students found to review. Check if the enrollment form has been submitted.
            </div>
          ) : (
            <>
              {/* Student list with disposition selectors */}
              <div className="space-y-3">
                {studentsToReview.map((student: any) => {
                  // Determine border/background color based on student status
                  let borderClass = 'border-gray-200 bg-gray-50';
                  if (student.isNotReturning) {
                    borderClass = 'border-slate-400 bg-slate-100';
                  } else if (student.isNew) {
                    borderClass = 'border-amber-300 bg-amber-50';
                  }

                  return (
                    <div
                      key={student.id}
                      className={`p-4 rounded-lg border ${borderClass}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {student.firstName} {student.lastName}
                            </span>
                            {student.isNotReturning && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-300 text-slate-700">
                                Not Returning
                              </span>
                            )}
                            {student.isNew && !student.isNotReturning && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-800">
                                New Student
                              </span>
                            )}
                          </div>
                          {student.gradeLevel && (
                            <p className="text-sm text-muted-foreground">Grade: {student.gradeLevel}</p>
                          )}
                          {student.isNotReturning && (
                            <p className="text-xs text-slate-500 mt-1">
                              Family indicated this student is not returning. Change to "Accept" to include them.
                            </p>
                          )}
                        </div>
                        <Select
                          value={dispositions[student.id] || (student.isNotReturning ? 'not_returning' : 'accept')}
                          onValueChange={(val: string) => handleDispositionChange(student.id, val)}
                        >
                          <SelectTrigger className="w-44">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DISPOSITION_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                <div className="flex items-center gap-2">
                                  {opt.value === 'accept' && <CheckCircle className="h-4 w-4 text-green-600" />}
                                  {opt.value === 'waitlist' && <Clock className="h-4 w-4 text-amber-600" />}
                                  {opt.value === 'deny' && <XCircle className="h-4 w-4 text-red-600" />}
                                  {opt.value === 'not_returning' && <MinusCircle className="h-4 w-4 text-slate-500" />}
                                  {opt.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-2">Summary</h4>
                <div className="flex flex-wrap gap-4 text-sm">
                  {counts.accept > 0 && (
                    <span className="text-green-700">
                      <CheckCircle className="h-4 w-4 inline mr-1" />
                      {counts.accept} accepting
                    </span>
                  )}
                  {counts.waitlist > 0 && (
                    <span className="text-amber-700">
                      <Clock className="h-4 w-4 inline mr-1" />
                      {counts.waitlist} waitlisting
                    </span>
                  )}
                  {counts.deny > 0 && (
                    <span className="text-red-700">
                      <XCircle className="h-4 w-4 inline mr-1" />
                      {counts.deny} denying
                    </span>
                  )}
                  {counts.not_returning > 0 && (
                    <span className="text-slate-600">
                      <MinusCircle className="h-4 w-4 inline mr-1" />
                      {counts.not_returning} not returning
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={completing}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={completing || studentsToReview.length === 0}
            className="bg-green-600 hover:bg-green-700"
          >
            {completing ? 'Completing Review...' : 'Complete Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ReviewActionsCardProps {
  enrollment: FamilyEnrollment;
  onCompleteReview: () => void;
  onDecline: () => void;
  onViewForm: () => void;
  onSendBack: () => void;
  completing: boolean;
  declining: boolean;
  sendingBack: boolean;
  /** With the checklist on, only View form and Decline are left. */
  checklistEnrollmentOn?: boolean;
}

export function ReviewActionsCard({ enrollment, checklistEnrollmentOn = false, onCompleteReview, onDecline, onViewForm, onSendBack, completing, declining, sendingBack }: ReviewActionsCardProps) {
  // The old flow's actions have no place on a year opened by the checklist invite.
  if (isNewStyle(enrollment)) return null;

  // An approved year can no longer move on with the checklist on; Decline closes it.
  const closeOnly = checklistEnrollmentOn && enrollment.state === 'APPROVED';
  if (enrollment.state !== 'NEEDS_REVIEW' && !closeOnly) {
    return null;
  }

  return (
    <Card className="border-orange-300 bg-orange-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
          <FileText className="h-5 w-5" />
          {closeOnly ? 'Enrollment Approved' : 'Review Required'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          {closeOnly
            ? 'This school enrolls families with the checklist. Declining closes this year and sends nothing.'
            : "This enrollment has been submitted by the family and is awaiting your review. Review each student's enrollment and make disposition decisions."}
        </p>
        <div className="flex flex-wrap gap-3">
          {!closeOnly && (
            <Button
              variant="outline"
              onClick={onViewForm}
            >
              <Eye className="h-4 w-4 mr-2" />
              View Submitted Form
            </Button>
          )}
          {!checklistEnrollmentOn && (
            <>
              <Button
                onClick={onCompleteReview}
                disabled={completing || declining || sendingBack}
                className="bg-green-600 hover:bg-green-700"
              >
                <ListTodo className="h-4 w-4 mr-2" />
                {completing ? 'Completing...' : 'Complete Review'}
              </Button>
              <Button
                variant="outline"
                onClick={onSendBack}
                disabled={completing || declining || sendingBack}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {sendingBack ? 'Sending...' : 'Send Back for Revisions'}
              </Button>
            </>
          )}
          <Button
            variant="outline"
            onClick={onDecline}
            disabled={completing || declining || sendingBack}
            className="border-destructive text-destructive hover:bg-destructive/10"
          >
            <XCircle className="h-4 w-4 mr-2" />
            {declining ? 'Declining...' : 'Decline'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Card to view submitted form for non-NEEDS_REVIEW states
interface ViewFormCardProps {
  enrollment: FamilyEnrollment;
  onViewForm: () => void;
}

export function ViewFormCard({ enrollment, onViewForm }: ViewFormCardProps) {
  // The old flow's actions have no place on a year opened by the checklist invite.
  if (isNewStyle(enrollment)) return null;

  // Show for states where form has been submitted
  const showForStates = ['APPROVED', 'PACKET_SENT', 'PACKET_VIEWED', 'PACKET_SUBMITTED', 'PACKET_COMPLETED'];

  if (!showForStates.includes(enrollment.state)) {
    return null;
  }

  const formLabel = enrollment.enrollmentKind === 'RE_ENROLLMENT' ? 'Re-enrollment Form' : 'Admissions Form';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <FileText className="h-4 w-4" />
          {formLabel}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Button
          variant="outline"
          size="sm"
          onClick={onViewForm}
        >
          <Eye className="h-4 w-4 mr-2" />
          View Submitted Form
        </Button>
      </CardContent>
    </Card>
  );
}

// Card to undo review (return to NEEDS_REVIEW state)
// Only shown in APPROVED state before packet is sent
interface UndoReviewCardProps {
  enrollment: FamilyEnrollment;
  onUndoReview: () => void;
  undoing: boolean;
  checklistEnrollmentOn?: boolean;
}

export function UndoReviewCard({ enrollment, checklistEnrollmentOn = false, onUndoReview, undoing }: UndoReviewCardProps) {
  // The old flow's actions have no place on a year opened by the checklist invite.
  if (isNewStyle(enrollment) || checklistEnrollmentOn) return null;

  // Only show for APPROVED state (before packet is sent)
  if (enrollment.state !== 'APPROVED') {
    return null;
  }

  // Check if there are paid or unpaid invoices (can't undo if so)
  const hasPaidOrUnpaidInvoices = (enrollment.invoices || []).some(
    (inv) => inv.status === 'PAID' || inv.status === 'UNPAID'
  );

  return (
    <Card className="border-amber-200 bg-amber-50/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-700">
          <RotateCcw className="h-4 w-4" />
          Undo Review
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">
          Return this enrollment to review status. Draft invoices will be deleted.
        </p>
        {hasPaidOrUnpaidInvoices ? (
          <p className="text-sm text-amber-700">
            Cannot undo review - there are paid or unpaid invoices.
          </p>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={onUndoReview}
            disabled={undoing}
            className="text-amber-700 border-amber-300 hover:bg-amber-100"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {undoing ? 'Undoing...' : 'Return to Review'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

interface InviteTokenCardProps {
  enrollment: FamilyEnrollment;
  onRevokeInvite: () => void;
  revoking: boolean;
  onResendInvite: () => void;
  resending: boolean;
  checklistEnrollmentOn?: boolean;
}

export function InviteTokenCard({ enrollment, checklistEnrollmentOn = false, onRevokeInvite, revoking, onResendInvite, resending }: InviteTokenCardProps) {
  // The old flow's actions have no place on a year opened by the checklist invite.
  if (isNewStyle(enrollment) || checklistEnrollmentOn) return null;

  if (!enrollment.inviteToken || !['INVITED', 'IN_PROGRESS'].includes(enrollment.state)) {
    return null;
  }

  const invitePath = enrollment.enrollmentKind === 'RE_ENROLLMENT' ? 'enroll' : 'apply';
  const inviteUrl = `${window.location.origin}/app/${invitePath}/${enrollment.inviteToken}`;

  return (
    <Card className="border-blue-300 bg-blue-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
          <Send className="h-5 w-5" />
          Invite Link
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-2">
          Share this link with the family to complete their {enrollment.enrollmentKind === 'RE_ENROLLMENT' ? 're-enrollment' : 'admissions form'}:
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white px-3 py-2 rounded text-sm font-mono truncate">
            {inviteUrl}
          </code>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigator.clipboard.writeText(inviteUrl)}
          >
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            asChild
          >
            <a href={inviteUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </div>
        <div className="mt-4 pt-4 border-t border-blue-200 flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onResendInvite}
            disabled={resending || revoking}
            className="text-blue-700 border-blue-300 hover:bg-blue-100"
          >
            <Send className="h-4 w-4 mr-2" />
            {resending ? 'Sending...' : 'Resend Invite'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onRevokeInvite}
            disabled={revoking || resending}
            className="text-amber-700 border-amber-300 hover:bg-amber-50"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {revoking ? 'Revoking...' : 'Revoke Invite'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Resend the invite email, or revoke to return to "Ready to Invite" status.
        </p>
      </CardContent>
    </Card>
  );
}

interface InviteActionsCardProps {
  enrollment: FamilyEnrollment;
  onQueue: () => void;
  onUnqueue: () => void;
  queueing: boolean;
  unqueueing: boolean;
  checklistEnrollmentOn?: boolean;
}

export function InviteActionsCard({ enrollment, checklistEnrollmentOn = false, onQueue, onUnqueue, queueing, unqueueing }: InviteActionsCardProps) {
  // The old flow's actions have no place on a year opened by the checklist invite.
  if (isNewStyle(enrollment) || checklistEnrollmentOn) return null;

  const canQueue = enrollment.canQueueForInvite;
  const canUnqueue = enrollment.state === 'READY_TO_INVITE' && enrollment.inviteStatus !== 'SENT';

  if (!canQueue && !canUnqueue) {
    return null;
  }

  if (canUnqueue) {
    return (
      <Card className="border-amber-300 bg-amber-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
            <Clock className="h-5 w-5" />
            Queued for Invite
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            This enrollment is queued and ready to receive an invite when sent.
            {!!enrollment.queuedAt && (
              <span className="block mt-1">
                Queued on {new Date(enrollment.queuedAt as string).toLocaleDateString()}
              </span>
            )}
          </p>
          <Button variant="outline" onClick={onUnqueue} disabled={unqueueing}>
            <X className="h-4 w-4 mr-2" />
            {unqueueing ? 'Removing...' : 'Remove from Queue'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (canQueue) {
    return (
      <Card className="border-blue-300 bg-blue-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
            <Clock className="h-5 w-5" />
            Ready to Queue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            This enrollment is ready to be queued for an invite. Once queued, you can send invites
            to all queued families at once from the Enrollments page.
          </p>
          <Button onClick={onQueue} disabled={queueing}>
            <Clock className="h-4 w-4 mr-2" />
            {queueing ? 'Queueing...' : 'Queue for Invite'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
}

interface PacketActionsCardProps {
  enrollment: FamilyEnrollment;
  familyPath: string;
  onSendPacket: () => void;
  sending: boolean;
  onRevokePacket: () => void;
  revokingPacket: boolean;
  checklistEnrollmentOn?: boolean;
}

export function PacketActionsCard({ enrollment, checklistEnrollmentOn = false, familyPath, onSendPacket, sending, onRevokePacket, revokingPacket }: PacketActionsCardProps) {
  // The old flow's actions have no place on a year opened by the checklist invite.
  if (isNewStyle(enrollment)) return null;

  if (enrollment.state === 'APPROVED') {
    if (checklistEnrollmentOn) return null;
    return (
      <Card className="border-green-300 bg-green-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2 text-green-700">
            <CheckCircle className="h-5 w-5" />
            Enrollment Approved
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            This enrollment has been approved. Send the enrollment packet for the family to sign and complete.
          </p>
          <Button onClick={onSendPacket} disabled={sending}>
            <Send className="h-4 w-4 mr-2" />
            {sending ? 'Sending...' : 'Send Enrollment Packet'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (['PACKET_SENT', 'PACKET_VIEWED', 'PACKET_SUBMITTED', 'PACKET_COMPLETED'].includes(enrollment.state)) {
    const packetUrl = enrollment.inviteToken
      ? `${window.location.origin}/app/packet/${enrollment.inviteToken}`
      : null;

    return (
      <Card className="border-purple-300 bg-purple-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2 text-purple-700">
            <FileText className="h-5 w-5" />
            Enrollment Packet {enrollment.state === 'PACKET_COMPLETED' ? 'Complete' : 'Sent'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-muted-foreground">Status:</span>{' '}
              <span className="font-medium">
                {enrollment.state === 'PACKET_SENT' && 'Awaiting family to view'}
                {enrollment.state === 'PACKET_VIEWED' && 'Viewed - awaiting signature'}
                {enrollment.state === 'PACKET_SUBMITTED' && 'Signed - invoices finalized'}
                {enrollment.state === 'PACKET_COMPLETED' && 'Complete'}
              </span>
            </p>
            {!!enrollment.packetSentAt && (
              <p>
                <span className="text-muted-foreground">Sent:</span>{' '}
                {new Date(enrollment.packetSentAt as string).toLocaleString()}
              </p>
            )}
            {!!enrollment.packetViewedAt && (
              <p>
                <span className="text-muted-foreground">Viewed:</span>{' '}
                {new Date(enrollment.packetViewedAt as string).toLocaleString()}
              </p>
            )}
            {!!enrollment.packetSubmittedAt && (
              <p>
                <span className="text-muted-foreground">Signed:</span>{' '}
                {new Date(enrollment.packetSubmittedAt as string).toLocaleString()}
              </p>
            )}
          </div>
          {!!enrollment.packetSubmittedAt && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to={`${familyPath}/enrollment-packet/${encodeURIComponent(enrollment.id)}`}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Signed Packet
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to={`${familyPath}/tuition-agreement/${encodeURIComponent(enrollment.id)}`}>
                  <FileText className="h-4 w-4 mr-2" />
                  Tuition Agreement
                </Link>
              </Button>
            </div>
          )}
          {packetUrl && enrollment.state !== 'PACKET_COMPLETED' && (
            <div className="mt-4 flex items-center gap-2">
              <code className="flex-1 bg-white px-3 py-2 rounded text-sm font-mono truncate">
                {packetUrl}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigator.clipboard.writeText(packetUrl)}
              >
                Copy
              </Button>
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <a href={packetUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </div>
          )}
          {/* Revoke packet button - only for PACKET_SENT or PACKET_VIEWED (not submitted/completed) */}
          {!checklistEnrollmentOn && (enrollment.state === 'PACKET_SENT' || enrollment.state === 'PACKET_VIEWED') && onRevokePacket && (
            <div className="mt-4 pt-4 border-t border-purple-200">
              <Button
                variant="outline"
                size="sm"
                onClick={onRevokePacket}
                disabled={revokingPacket}
                className="text-amber-700 border-amber-300 hover:bg-amber-50"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {revokingPacket ? 'Revoking...' : 'Revoke Packet'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Revoke this packet and return to "Approved" status. The form will be voided.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return null;
}

interface AdditionalPaymentOptionsCardProps {
  enrollment: FamilyEnrollment | null | undefined;
  allPaymentOptions: PaymentOption[];
  onUpdate: (keys: string[]) => void;
  updating: boolean;
  /** With the school's switch on, the card also sets the family's enrollment fee. */
  showFeeSetting?: boolean;
  onFeeSaved?: () => void;
}

function AdditionalPaymentOptionsCard({
  enrollment,
  allPaymentOptions = [],
  onUpdate,
  updating,
  showFeeSetting = false,
  onFeeSaved
}: AdditionalPaymentOptionsCardProps) {
  // Get admin-only options (not selectable on enrollment)
  const adminOnlyOptions = useMemo(() =>
    allPaymentOptions.filter((opt) => opt.selectableOnEnrollment === false),
    [allPaymentOptions]
  );

  // Currently assigned additional options
  const assignedKeys = useMemo(() => enrollment?.additionalPaymentOptionKeys || [], [enrollment?.additionalPaymentOptionKeys]);

  const handleToggleOption = useCallback((key: string, checked: boolean) => {
    const newKeys = checked
      ? [...assignedKeys, key]
      : assignedKeys.filter((k: string) => k !== key);
    onUpdate(newKeys);
  }, [assignedKeys, onUpdate]);

  const fee = showFeeSetting ? enrollment?.enrollmentFee : null;
  // Nothing to set: no admin-only options and no fee setting to show
  if (adminOnlyOptions.length === 0 && !fee) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Settings2 className="h-5 w-5" />
          Special Payment Options
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {adminOnlyOptions.length > 0 && (
        <div>
        <p className="text-sm text-muted-foreground mb-4">
          Enable admin-only payment options for this family (e.g., teacher discounts, special rates).
          These options are not shown to families by default during enrollment.
        </p>
        <div className="space-y-3">
          {adminOnlyOptions.map((option) => (
            <div key={option.key} className="flex items-center gap-3">
              <Checkbox
                id={`payment-opt-${option.key}`}
                checked={assignedKeys.includes(option.key)}
                onCheckedChange={(checked: boolean) => handleToggleOption(option.key, checked)}
                disabled={updating}
              />
              <Label htmlFor={`payment-opt-${option.key}`} className="cursor-pointer">
                <span className="font-medium">{option.name}</span>
                <span className="text-xs text-muted-foreground ml-2">
                  ({option.scheduleType})
                </span>
              </Label>
            </div>
          ))}
        </div>
        {updating && (
          <p className="text-sm text-muted-foreground mt-2">Saving...</p>
        )}
        </div>
        )}
        {fee && enrollment && (
          <EnrollmentFeeSetting
            familyEnrollmentId={enrollment.id}
            fee={fee}
            regularCents={enrollment.schoolYear.enrollmentFeeSetting.amountCents}
            onSaved={onFeeSaved}
          />
        )}
      </CardContent>
    </Card>
  );
}

interface StudentEnrollmentActionsProps {
  enrollment: StudentEnrollment;
  onAccept: (id: string) => void;
  onWaitlist: (id: string) => void;
  onDeny: (enrollment: StudentEnrollment) => void;
  onWithdraw: (enrollment: StudentEnrollment) => void;
  loading: boolean;
}

function StudentEnrollmentActions({ enrollment, onAccept, onWaitlist, onDeny, onWithdraw, loading }: StudentEnrollmentActionsProps) {
  const actions = getAvailableStudentEnrollmentActions(enrollment.state);

  if (!hasAvailableActions(enrollment.state)) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={loading}>
          Actions
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.canAccept && (
          <DropdownMenuItem onClick={() => onAccept(enrollment.id)}>
            <UserCheck className="h-4 w-4 mr-2 text-green-600" />
            Accept
          </DropdownMenuItem>
        )}
        {actions.canWaitlist && (
          <DropdownMenuItem onClick={() => onWaitlist(enrollment.id)}>
            <ListTodo className="h-4 w-4 mr-2 text-yellow-600" />
            Waitlist
          </DropdownMenuItem>
        )}
        {actions.canDeny && (
          <DropdownMenuItem onClick={() => onDeny(enrollment)}>
            <UserX className="h-4 w-4 mr-2 text-red-600" />
            Deny
          </DropdownMenuItem>
        )}
        {actions.canWithdraw && (
          <DropdownMenuItem onClick={() => onWithdraw(enrollment)}>
            <XCircle className="h-4 w-4 mr-2 text-gray-600" />
            Withdraw
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface StudentEnrollmentsTableProps {
  enrollments: StudentEnrollment[] | null | undefined;
  schoolSlug: string;
  canDelete: boolean;
  gradeLevels: GradeLevel[];
  /** The year's programs, for a child the family chose none for on Who's coming. */
  programs?: { key: string; name: string }[];
  /** Each child's program shows on a year enrolling through its checklist, never the old flow's. */
  programsShown?: boolean;
  editedData: Record<string, any>;
  onDataChange: (enrollmentId: string, field: string, value: any) => void;
  onDeleteStudent: (enrollment: StudentEnrollment) => void;
  onAccept: (id: string) => void;
  onWaitlist: (id: string) => void;
  onDeny: (enrollment: StudentEnrollment) => void;
  onWithdraw: (enrollment: StudentEnrollment) => void;
  actionLoading: boolean;
  /** With the school's switch on, the office sets any status from a menu in place of the old actions. */
  checklistEnrollmentOn?: boolean;
  onSetStatus?: (enrollment: StudentEnrollment, status: string) => void;
}

// The program a child is priced on, under their name. The family's choice on
// Who's coming prices them first and stays as it is; for a child they chose
// none for, the office picks one of the year's programs.
function ChildProgram({ enrollment, programs, value, onChange }: {
  enrollment: StudentEnrollment;
  programs: { key: string; name: string }[];
  value: string | null | undefined;
  onChange: (key: string) => void;
}) {
  const nameOf = (key: string) => programs.find((program) => program.key === key)?.name ?? key;
  // A key the year no longer offers reads as none chosen.
  const chosen = programs.some((program) => program.key === value) ? value : '';
  if (enrollment.familyTuitionOptionKey) {
    return (
      <div className="mt-1 flex flex-col">
        <span className="text-xs text-muted-foreground">Program chosen by the family</span>
        <span className="text-sm">{nameOf(enrollment.familyTuitionOptionKey)}</span>
      </div>
    );
  }
  return (
    <div className="mt-1 flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">Program</span>
      <select
        aria-label={`Program for ${enrollment.person.firstName} ${enrollment.person.lastName}`}
        className="h-7 w-[140px] rounded-md border border-input bg-background px-2 text-sm"
        value={chosen ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {!chosen && <option value="" disabled>Not chosen yet</option>}
        {programs.map((program) => (
          <option key={program.key} value={program.key}>{program.name}</option>
        ))}
      </select>
    </div>
  );
}

export function StudentEnrollmentsTable({
  enrollments,
  schoolSlug,
  canDelete,
  gradeLevels,
  programs = [],
  programsShown = false,
  editedData,
  onDataChange,
  onDeleteStudent,
  onAccept,
  onWaitlist,
  onDeny,
  onWithdraw,
  actionLoading,
  checklistEnrollmentOn = false,
  onSetStatus
}: StudentEnrollmentsTableProps) {
  if (!enrollments || enrollments.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No students in this enrollment yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Enrolled Students</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Student</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Grade Level</TableHead>
              <TableHead>Type</TableHead>
              {checklistEnrollmentOn && <TableHead>Starts</TableHead>}
              <TableHead>Needs Review</TableHead>
              <TableHead className={checklistEnrollmentOn ? 'w-[180px]' : 'w-[100px]'}>{checklistEnrollmentOn ? 'Set status' : 'Actions'}</TableHead>
              {canDelete && <TableHead className="w-[50px]"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {enrollments.map((enrollment) => {
              const edited = editedData[enrollment.id] || {};
              const hasChanges = Object.keys(edited).length > 0;

              return (
                <TableRow key={enrollment.id} className={hasChanges ? 'bg-primary/5' : ''}>
                  <TableCell>
                    <Link
                      to={`/app/${schoolSlug}/students/${enrollment.person.id}/enrollment`}
                      className="font-medium hover:underline"
                    >
                      {enrollment.person.firstName} {enrollment.person.lastName}
                    </Link>
                    {programsShown && (
                      <ChildProgram
                        enrollment={enrollment}
                        programs={programs}
                        value={'tuitionOptionKey' in edited ? edited.tuitionOptionKey : enrollment.tuitionOptionKey}
                        onChange={(key) => onDataChange(enrollment.id, 'tuitionOptionKey', key)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <EnrollmentStatusBadge state={enrollment.state} type="student" />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={edited.gradeLevel ?? enrollment.gradeLevel ?? ''}
                      onValueChange={(value: string) => onDataChange(enrollment.id, 'gradeLevel', value)}
                    >
                      <SelectTrigger className="h-7 w-[120px]">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {gradeLevels.map((grade) => (
                          <SelectItem key={grade.description} value={grade.description}>
                            {grade.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={edited.enrollmentKind ?? enrollment.enrollmentKind}
                      onValueChange={(value: string) => onDataChange(enrollment.id, 'enrollmentKind', value)}
                    >
                      <SelectTrigger className="h-7 w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STUDENT_ENROLLMENT_KINDS.map((kind) => (
                          <SelectItem key={kind.value} value={kind.value}>
                            {kind.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  {checklistEnrollmentOn && (
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <Input
                        type="date"
                        className="h-7 w-[140px]"
                        aria-label={`Starts for ${enrollment.person.firstName} ${enrollment.person.lastName}`}
                        value={'startsOn' in edited ? (edited.startsOn ?? '') : (enrollment.startsOn ?? '')}
                        onChange={(e) => onDataChange(enrollment.id, 'startsOn', e.target.value || null)}
                      />
                      {!('startsOn' in edited ? edited.startsOn : enrollment.startsOn) && (
                        <span className="text-xs text-muted-foreground">Whole year</span>
                      )}
                    </div>
                  </TableCell>
                  )}
                  <TableCell>
                    <Checkbox
                      checked={edited.needsReview ?? enrollment.needsReview ?? false}
                      onCheckedChange={(checked: boolean) => onDataChange(enrollment.id, 'needsReview', checked)}
                    />
                  </TableCell>
                  <TableCell>
                    {checklistEnrollmentOn ? (
                      <StudentStatusMenu
                        compact
                        studentName={`${enrollment.person.firstName} ${enrollment.person.lastName}`}
                        state={enrollment.state}
                        saving={actionLoading}
                        onConfirm={(status) => onSetStatus?.(enrollment, status)}
                      />
                    ) : (
                      <StudentEnrollmentActions
                        enrollment={enrollment}
                        onAccept={onAccept}
                        onWaitlist={onWaitlist}
                        onDeny={onDeny}
                        onWithdraw={onWithdraw}
                        loading={actionLoading}
                      />
                    )}
                  </TableCell>
                  {canDelete && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        aria-label={`Remove ${enrollment.person.firstName} ${enrollment.person.lastName} from this year`}
                        onClick={() => onDeleteStudent(enrollment)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function FamilyEnrollmentTab({
  family,
  enrollment,
  selectedYear,
  schoolSlug,
  schoolId,
  checklistEnrollmentOn = false,
  gradeLevels = [],
  paymentOptions = [],
  onOpenAddStudentsWizard,
  onRefetch,
}: FamilyEnrollmentTabProps) {
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; type: string | null; target: StudentEnrollment | FamilyEnrollment | null }>({ open: false, type: null, target: null });
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [actionConfirmDialog, setActionConfirmDialog] = useState<{ open: boolean; action: string | null; enrollment: StudentEnrollment | null }>({ open: false, action: null, enrollment: null });
  const [editedFamilyEnrollment, setEditedFamilyEnrollment] = useState<Record<string, any>>({});
  const [editedStudentEnrollments, setEditedStudentEnrollments] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [showFormReview, setShowFormReview] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [updatingPaymentOptions, setUpdatingPaymentOptions] = useState(false);

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, type: null, target: null });
    setDeleteError(null);
  };

  const [deleteStudentEnrollment, { loading: deletingStudent }] = useMutation(DeleteStudentEnrollmentMutation, {
    onCompleted: (result) => {
      if (result.deleteStudentEnrollment?.success) {
        onRefetch?.();
        closeDeleteDialog();
        return;
      }

      setDeleteError(
        result.deleteStudentEnrollment?.userErrors?.map((item) => item.message).join(', ') ||
          'Unable to remove this student.',
      );
    },
    onError: (error) => setDeleteError(error.message || 'Unable to remove this student.'),
  });

  const [deleteFamilyEnrollment, { loading: deletingFamily }] = useMutation(DeleteFamilyEnrollmentMutation, {
    onCompleted: (result) => {
      if (result.deleteFamilyEnrollment?.success) {
        onRefetch?.();
        closeDeleteDialog();
        return;
      }

      setDeleteError(
        result.deleteFamilyEnrollment?.userErrors?.map((item) => item.message).join(', ') ||
          'Unable to delete this enrollment.',
      );
    },
    onError: (error) => setDeleteError(error.message || 'Unable to delete this enrollment.'),
  });

  const [updateFamilyEnrollment] = useMutation(UpdateFamilyEnrollmentMutation);
  const [updateStudentEnrollment] = useMutation(UpdateStudentEnrollmentMutation);
  // DEPRECATED: Old approve mutation - kept for reference
  // const [approveFamilyEnrollment, { loading: approving }] = useMutation(ApproveFamilyEnrollmentMutation, {
  //   onCompleted: () => onRefetch?.(),
  // });
  const [completeEnrollmentReview, { loading: completing }] = useMutation(CompleteEnrollmentReviewMutation, {
    onCompleted: () => {
      setShowReviewModal(false);
      onRefetch?.();
    },
  });
  const [undoEnrollmentReview, { loading: undoing }] = useMutation(UndoEnrollmentReviewMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [declineFamilyEnrollment, { loading: declining }] = useMutation(DeclineFamilyEnrollmentMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [sendFormBackToFamily, { loading: sendingBack }] = useMutation(SendFormBackToFamilyMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [sendEnrollmentPacket, { loading: sendingPacket }] = useMutation(SendEnrollmentPacketMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [queueFamilyForInvite, { loading: queueing }] = useMutation(QueueFamilyForInviteMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [unqueueFamily, { loading: unqueueing }] = useMutation(UnqueueFamilyMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [revokeReenrollmentInvite, { loading: revoking }] = useMutation(RevokeReenrollmentInviteMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [resendFamilyInvite, { loading: resending }] = useMutation(ResendFamilyInvite, {
    onCompleted: () => onRefetch?.(),
  });
  const [revokeEnrollmentPacket, { loading: revokingPacket }] = useMutation(RevokeEnrollmentPacketMutation, {
    onCompleted: () => onRefetch?.(),
  });

  // Student enrollment action mutations
  const [acceptStudentEnrollment, { loading: accepting }] = useMutation(AcceptStudentEnrollmentMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [waitlistStudentEnrollment, { loading: waitlisting }] = useMutation(WaitlistStudentEnrollmentMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [denyStudentEnrollment, { loading: denying }] = useMutation(DenyStudentEnrollmentMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [withdrawStudentEnrollment, { loading: withdrawing }] = useMutation(WithdrawStudentEnrollmentMutation, {
    onCompleted: () => onRefetch?.(),
  });

  const [setStudentEnrollmentStatus, { loading: settingStatus }] = useSetStudentEnrollmentStatus();
  const studentActionLoading = accepting || waitlisting || denying || withdrawing || settingStatus;

  const handleSetStudentStatus = (studentEnrollment: StudentEnrollment, status: string) => {
    setStudentEnrollmentStatus({
      variables: { studentEnrollmentId: studentEnrollment.id, status: status as VariablesOf<typeof SetStudentEnrollmentStatusMutation>['status'] },
      onCompleted: () => onRefetch?.(),
    });
  };

  // Check for family enrollment changes
  const hasFamilyChanges = useMemo(() => {
    return Object.keys(editedFamilyEnrollment).length > 0;
  }, [editedFamilyEnrollment]);

  // Check for student enrollment changes
  const hasStudentChanges = useMemo(() => {
    return Object.keys(editedStudentEnrollments).length > 0;
  }, [editedStudentEnrollments]);

  const hasAnyChanges = hasFamilyChanges || hasStudentChanges;

  // Handle family enrollment data change
  const handleFamilyDataChange = useCallback((field: string, value: any) => {
    setEditedFamilyEnrollment(prev => {
      const originalValue = (enrollment as Record<string, any> | null | undefined)?.[field];
      if (value === originalValue) {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [field]: value };
    });
  }, [enrollment]);

  // Handle student enrollment data change
  const handleStudentDataChange = useCallback((enrollmentId: string, field: string, value: any) => {
    setEditedStudentEnrollments(prev => {
      const currentData = prev[enrollmentId] || {};
      const studentEnrollment = enrollment?.studentEnrollments?.find((se) => se.id === enrollmentId);
      const originalValue = (studentEnrollment as Record<string, any> | undefined)?.[field];

      if (value === originalValue) {
        const { [field]: _, ...restFields } = currentData;
        if (Object.keys(restFields).length === 0) {
          const { [enrollmentId]: __, ...restEnrollments } = prev;
          return restEnrollments;
        }
        return { ...prev, [enrollmentId]: restFields };
      }

      return {
        ...prev,
        [enrollmentId]: { ...currentData, [field]: value }
      };
    });
  }, [enrollment]);

  // Save all changes
  const handleSaveChanges = useCallback(async () => {
    setIsSaving(true);
    setSaveErrors([]);
    try {
      const promises: Promise<any>[] = [];

      // Update family enrollment
      if (hasFamilyChanges) {
        promises.push(
          updateFamilyEnrollment({
            variables: {
              input: {
                familyEnrollmentId: enrollment!.id,
                ...editedFamilyEnrollment
              }
            }
          })
        );
      }

      // Update student enrollments
      for (const [enrollmentId, changes] of Object.entries(editedStudentEnrollments)) {
        promises.push(
          updateStudentEnrollment({
            variables: {
              studentEnrollmentId: enrollmentId,
              ...changes
            }
          })
        );
      }

      const results = await Promise.all(promises);
      // A refusal keeps the edits on screen with the server's words.
      const refused = results.flatMap((result) => result?.data?.updateStudentEnrollment?.userErrors ?? []).map((e: { message: string }) => e.message);
      if (refused.length > 0) {
        setSaveErrors(refused);
        return;
      }
      setEditedFamilyEnrollment({});
      setEditedStudentEnrollments({});
      onRefetch?.();
    } catch (error) {
      console.error('Failed to save changes:', error);
      setSaveErrors([error instanceof Error ? error.message : 'The changes were not saved. Please try again.']);
    } finally {
      setIsSaving(false);
    }
  }, [hasFamilyChanges, editedFamilyEnrollment, editedStudentEnrollments, enrollment?.id, updateFamilyEnrollment, updateStudentEnrollment, onRefetch]);

  // Cancel changes
  const handleCancelChanges = useCallback(() => {
    setEditedFamilyEnrollment({});
    setEditedStudentEnrollments({});
  }, []);

  const handleDeleteStudent = (studentEnrollment: StudentEnrollment) => {
    setDeleteDialog({ open: true, type: 'student', target: studentEnrollment });
  };

  const handleDeleteFamily = () => {
    setDeleteDialog({ open: true, type: 'family', target: enrollment! });
  };

  // Opens the review modal
  const handleOpenReviewModal = () => {
    setShowReviewModal(true);
  };

  // Completes the review with student dispositions
  const handleCompleteReview = (studentDispositions: any) => {
    completeEnrollmentReview({
      variables: {
        input: {
          familyEnrollmentId: enrollment!.id,
          studentDispositions
        }
      }
    });
  };

  // Undoes a completed review
  const handleUndoReview = () => {
    undoEnrollmentReview({ variables: { familyEnrollmentId: enrollment!.id } });
  };

  const handleDecline = () => {
    declineFamilyEnrollment({ variables: { familyEnrollmentId: enrollment!.id } });
  };

  const handleSendBack = () => {
    sendFormBackToFamily({ variables: { familyEnrollmentId: enrollment!.id } });
  };

  const handleSendPacket = () => {
    const invoices = enrollment?.invoices || [];
    const hasEnrollmentFeeInvoice = invoices.some((inv) => inv?.key === 'enrollment_fee');
    const hasTuitionInvoices = invoices.some((inv) => typeof inv?.key === 'string' && inv.key.startsWith('tuition'));

    const variables: any = { familyEnrollmentId: enrollment!.id };

    // Require explicit confirmation if either invoice type is missing.
    if (!hasEnrollmentFeeInvoice || !hasTuitionInvoices) {
      const confirmed = window.confirm(
        [
          "This enrollment is missing required invoice(s):",
          !hasEnrollmentFeeInvoice ? "- Enrollment fee invoice" : null,
          !hasTuitionInvoices ? "- Tuition invoice(s)" : null,
          "",
          "Send the enrollment packet anyway?"
        ].filter(Boolean).join("\n")
      );
      if (!confirmed) return;
      variables.forceSendWithoutInvoices = true;
    }

    sendEnrollmentPacket({ variables });
  };

  const handleQueue = () => {
    queueFamilyForInvite({ variables: { familyEnrollmentId: enrollment!.id } });
  };

  const handleUnqueue = () => {
    unqueueFamily({ variables: { familyEnrollmentId: enrollment!.id } });
  };

  const handleRevokeInvite = () => {
    revokeReenrollmentInvite({ variables: { familyEnrollmentId: enrollment!.id } });
  };

  const handleResendInvite = () => {
    resendFamilyInvite({ variables: { input: { familyEnrollmentId: enrollment!.id } } });
  };

  const handleRevokePacket = () => {
    revokeEnrollmentPacket({ variables: { familyEnrollmentId: enrollment!.id } });
  };

  const handleUpdateAdditionalPaymentOptions = useCallback(async (keys: string[]) => {
    setUpdatingPaymentOptions(true);
    try {
      await updateFamilyEnrollment({
        variables: {
          input: {
            familyEnrollmentId: enrollment!.id,
            additionalPaymentOptionKeys: keys
          }
        }
      });
      onRefetch?.();
    } catch (error) {
      console.error('Failed to update additional payment options:', error);
    } finally {
      setUpdatingPaymentOptions(false);
    }
  }, [enrollment?.id, updateFamilyEnrollment, onRefetch]);

  // Student enrollment action handlers
  const handleAcceptStudent = (studentEnrollmentId: string) => {
    acceptStudentEnrollment({ variables: { studentEnrollmentId } });
  };

  const handleWaitlistStudent = (studentEnrollmentId: string) => {
    waitlistStudentEnrollment({ variables: { studentEnrollmentId } });
  };

  const handleDenyStudent = (studentEnrollment: StudentEnrollment) => {
    setActionConfirmDialog({ open: true, action: 'deny', enrollment: studentEnrollment });
  };

  const handleWithdrawStudent = (studentEnrollment: StudentEnrollment) => {
    setActionConfirmDialog({ open: true, action: 'withdraw', enrollment: studentEnrollment });
  };

  const confirmAction = () => {
    const { action, enrollment: targetEnrollment } = actionConfirmDialog;
    if (action === 'deny') {
      denyStudentEnrollment({ variables: { studentEnrollmentId: targetEnrollment!.id } });
    } else if (action === 'withdraw') {
      withdrawStudentEnrollment({ variables: { studentEnrollmentId: targetEnrollment!.id } });
    }
    setActionConfirmDialog({ open: false, action: null, enrollment: null });
  };

  const confirmDelete = () => {
    setDeleteError(null);
    if (deleteDialog.type === 'student') {
      deleteStudentEnrollment({
        variables: { studentEnrollmentId: deleteDialog.target!.id },
      });
    } else if (deleteDialog.type === 'family') {
      deleteFamilyEnrollment({
        variables: { familyEnrollmentId: deleteDialog.target!.id },
      });
    }
  };

  if (!selectedYear) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Select a school year to view enrollment information.
        </CardContent>
      </Card>
    );
  }

  if (!enrollment) {
    return (
      <EmptyEnrollmentState
        selectedYear={selectedYear}
        familyName={family.name}
        onStartEnrollment={onOpenAddStudentsWizard}
      />
    );
  }

  // The server's rules: the year can be deleted only before the
  // family is invited and while it has no invoices; a child, unless the year is enrolled.
  const canDelete = enrollment.canDelete;
  const canDeleteStudents = enrollment.access !== 'ENROLLED';

  // Find children who are not yet enrolled
  const enrolledPersonIds = new Set(
    (enrollment.studentEnrollments || []).map((se) => se.person.id)
  );
  const unenrolledChildren = (family.children || []).filter(
    (child) => !enrolledPersonIds.has(child.person.id)
  );

  return (
    <div className="space-y-6">
      {/* Floating save bar when there are changes */}
      {hasAnyChanges && (
        <Card className="sticky top-0 z-10 border-primary bg-primary/5">
          <CardContent className="flex items-center justify-between py-3">
            <div>
              <span className="text-sm font-medium">You have unsaved changes</span>
              {saveErrors.map((message) => (
                <p key={message} role="alert" className="text-sm text-destructive">{message}</p>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancelChanges} disabled={isSaving}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveChanges} disabled={isSaving}>
                <Save className="h-4 w-4 mr-1" />
                {isSaving ? 'Saving...' : 'Save All Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Review Actions (shown when NEEDS_REVIEW) */}
      <ReviewActionsCard
        enrollment={enrollment}
        checklistEnrollmentOn={checklistEnrollmentOn}
        onCompleteReview={handleOpenReviewModal}
        onDecline={handleDecline}
        onSendBack={handleSendBack}
        onViewForm={() => setShowFormReview(true)}
        completing={completing}
        declining={declining}
        sendingBack={sendingBack}
      />

      {/* View Form (shown for approved/packet states) */}
      <ViewFormCard
        enrollment={enrollment}
        onViewForm={() => setShowFormReview(true)}
      />

      {/* Undo Review (shown for APPROVED state before packet sent) */}
      <UndoReviewCard
        enrollment={enrollment}
        checklistEnrollmentOn={checklistEnrollmentOn}
        onUndoReview={handleUndoReview}
        undoing={undoing}
      />

      {/* Invite Actions (queue/unqueue for invite) */}
      <InviteActionsCard
        enrollment={enrollment}
        checklistEnrollmentOn={checklistEnrollmentOn}
        onQueue={handleQueue}
        onUnqueue={handleUnqueue}
        queueing={queueing}
        unqueueing={unqueueing}
      />

      {/* Invite Link (shown when INVITED or IN_PROGRESS) */}
      <InviteTokenCard
        enrollment={enrollment}
        checklistEnrollmentOn={checklistEnrollmentOn}
        onRevokeInvite={handleRevokeInvite}
        revoking={revoking}
        onResendInvite={handleResendInvite}
        resending={resending}
      />

      {/* Packet Actions (shown for APPROVED and PACKET_* states) */}
      <PacketActionsCard
        enrollment={enrollment}
        checklistEnrollmentOn={checklistEnrollmentOn}
        familyPath={`/app/${schoolSlug}/families/${family.slug}`}
        onSendPacket={handleSendPacket}
        sending={sendingPacket}
        onRevokePacket={handleRevokePacket}
        revokingPacket={revokingPacket}
      />

      <EditableFamilyEnrollmentCard
        enrollment={enrollment}
        checklistEnrollmentOn={checklistEnrollmentOn}
        editedData={editedFamilyEnrollment}
        onDataChange={handleFamilyDataChange}
        hasChanges={hasFamilyChanges}
        onSave={handleSaveChanges}
        onCancel={handleCancelChanges}
        isSaving={isSaving}
      />

      {/* Special Payment Options for this family */}
      <AdditionalPaymentOptionsCard
        enrollment={enrollment}
        allPaymentOptions={paymentOptions}
        onUpdate={handleUpdateAdditionalPaymentOptions}
        updating={updatingPaymentOptions}
        showFeeSetting={checklistEnrollmentOn}
        onFeeSaved={onRefetch}
      />

      {/* Delete Family Enrollment Button (only in draft) */}
      {canDelete && (
        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div>
              <p className="font-medium text-destructive">Delete Enrollment</p>
              <p className="text-sm text-muted-foreground">
                Remove this family's enrollment for {selectedYear.name}
              </p>
            </div>
            <Button variant="destructive" onClick={handleDeleteFamily}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Enrollment
            </Button>
          </CardContent>
        </Card>
      )}

      {enrollment.checklist && schoolId && (
        <ChecklistCard
          checklist={enrollment.checklist}
          familyPath={`/app/${schoolSlug}/families/${family.slug}`}
          familyEnrollmentId={enrollment.id}
          schoolId={schoolId}
          showInvite={checklistEnrollmentOn && enrollment.accessSource === 'SET'}
          canResend={checklistEnrollmentOn && enrollment.accessSource === 'SET' && ['ENROLLING', 'ENROLLED'].includes(enrollment.access)}
          showOfficeActions={checklistEnrollmentOn}
          canMarkEnrolled={checklistEnrollmentOn && enrollment.accessSource === 'SET' && enrollment.access === 'ENROLLING'}
          familyName={family.name}
          yearName={selectedYear.name}
          childrenOfFamily={enrollment.studentEnrollments.map((se) => ({ id: se.person.id, name: se.person.firstName }))}
          comingChildren={enrollment.studentEnrollments.filter((se) => se.state === 'ACCEPTED_PENDING_DEPOSIT').map((se) => se.person.firstName)}
          plans={paymentOptions}
          invoices={enrollment.invoices}
          onChanged={onRefetch}
        />
      )}

      <StudentEnrollmentsTable
        enrollments={enrollment.studentEnrollments}
        schoolSlug={schoolSlug}
        canDelete={canDeleteStudents}
        gradeLevels={gradeLevels}
        programs={enrollment.schoolYear?.tuitionOptionsSetting?.items ?? []}
        programsShown={checklistEnrollmentOn && enrollment.accessSource === 'SET'}
        editedData={editedStudentEnrollments}
        onDataChange={handleStudentDataChange}
        onDeleteStudent={handleDeleteStudent}
        onAccept={handleAcceptStudent}
        onWaitlist={handleWaitlistStudent}
        onDeny={handleDenyStudent}
        onWithdraw={handleWithdrawStudent}
        actionLoading={studentActionLoading}
        checklistEnrollmentOn={checklistEnrollmentOn}
        onSetStatus={handleSetStudentStatus}
      />

      <div className="flex flex-wrap justify-end gap-2">
        {schoolId && (
          <InviteToEnrollAction
            checklistEnrollmentOn={checklistEnrollmentOn}
            access={enrollment.access}
            accessSource={enrollment.accessSource}
            schoolId={schoolId}
            familyEnrollmentId={enrollment.id}
            familyName={family.name}
            yearName={selectedYear.name}
            onSuccess={onRefetch}
          />
        )}
        <AddChildToYearAction
          checklistEnrollmentOn={checklistEnrollmentOn}
          familyEnrollmentId={enrollment.id}
          familyName={family.name}
          yearName={selectedYear.name}
          gradeLevels={gradeLevels}
          programs={enrollment.schoolYear?.tuitionOptionsSetting?.items ?? []}
          yearStartsOn={enrollment.schoolYear?.startDate}
          programRequired={enrollment.checklist?.completedAt != null || enrollment.checklist?.items.some((item) => item.stepType === 'agreement' && item.tuitionAgreement?.state === 'SIGNED') === true}
          onSuccess={onRefetch}
        />
      </div>

      {/* Add Students Action */}
      {unenrolledChildren.length > 0 && (
        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div>
              <p className="font-medium">
                {unenrolledChildren.length} child{unenrolledChildren.length > 1 ? 'ren' : ''} not yet added
              </p>
              <p className="text-sm text-muted-foreground">
                {unenrolledChildren.map((c) => c.person.firstName).join(', ')}
              </p>
            </div>
            <Button onClick={onOpenAddStudentsWizard}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Students
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Invoices Section */}
      <InvoicesSection
        enrollment={enrollment}
        studentEnrollments={enrollment.studentEnrollments}
        schoolYear={selectedYear}
        paymentOptions={paymentOptions}
        checklistEnrollmentOn={checklistEnrollmentOn}
        onRefetch={onRefetch}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open: boolean) => !open && closeDeleteDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteDialog.type === 'student' ? 'Remove Student from Year' : 'Delete Family Enrollment'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.type === 'student' ? (
                <>
                  Are you sure you want to remove{' '}
                  <strong>{(deleteDialog.target as StudentEnrollment | null)?.person?.firstName} {(deleteDialog.target as StudentEnrollment | null)?.person?.lastName}</strong>{' '}
                  from this year's enrollment?
                </>
              ) : (
                <>
                  Are you sure you want to delete the enrollment for {selectedYear!.name}?
                  This will also remove all {enrollment?.studentEnrollments?.length || 0} student enrollments.
                </>
              )}
              {' '}This action cannot be undone.
            </AlertDialogDescription>
            {deleteError && (
              <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {deleteError}
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                confirmDelete();
              }}
              disabled={deletingStudent || deletingFamily}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(deletingStudent || deletingFamily) ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin Re-enrollment Form Review Dialog */}
      {/* TODO: Refactor — both form viewers should be unified into a single read-only
          component that reuses the same presentation as the parent portal forms.
          See KAN epic "Refactor form components: separate presentation from logic" */}
      {enrollment.enrollmentKind === 'RE_ENROLLMENT' ? (
        <AdminReenrollmentReview
          familyEnrollmentId={enrollment.id}
          open={showFormReview}
          onClose={() => setShowFormReview(false)}
        />
      ) : (
        <AdminAdmissionsReview
          familyEnrollmentId={enrollment.id}
          open={showFormReview}
          onClose={() => setShowFormReview(false)}
        />
      )}

      {/* Complete Review Modal */}
      <CompleteReviewModal
        open={showReviewModal}
        onOpenChange={setShowReviewModal}
        enrollment={enrollment}
        onComplete={handleCompleteReview}
        completing={completing}
      />

      {/* Action Confirmation Dialog (Deny/Withdraw) */}
      <AlertDialog
        open={actionConfirmDialog.open}
        onOpenChange={(open: boolean) => !open && setActionConfirmDialog({ open: false, action: null, enrollment: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionConfirmDialog.action === 'deny' ? 'Deny Student Enrollment' : 'Withdraw Student'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionConfirmDialog.action === 'deny' ? (
                <>
                  Are you sure you want to deny{' '}
                  <strong>{actionConfirmDialog.enrollment?.person?.firstName} {actionConfirmDialog.enrollment?.person?.lastName}</strong>'s
                  enrollment? This action cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to withdraw{' '}
                  <strong>{actionConfirmDialog.enrollment?.person?.firstName} {actionConfirmDialog.enrollment?.person?.lastName}</strong>?
                  This will remove them from the enrollment.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAction}
              disabled={denying || withdrawing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {denying || withdrawing
                ? (actionConfirmDialog.action === 'deny' ? 'Denying...' : 'Withdrawing...')
                : (actionConfirmDialog.action === 'deny' ? 'Deny Enrollment' : 'Withdraw Student')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default FamilyEnrollmentTab;
